
import { computed, defineComponent, reactive, toRefs, onMounted } from "vue";
import {
  fetchSensorTagOptions,
  fetchTasks,
  fetchTaskRules,
  fetchTaskRuns,
  fetchTaskRun,
  fetchTaskRunCounts,
  postRerunTask,
} from "@/helpers/api";
import { formatTime } from "@/helpers/time";
import { Sensor } from "@/interfaces/Sensor";
import { Task } from "@/interfaces/Task";
import { TaskRule } from "@/interfaces/TaskRule";
import { TaskRun, LocalTaskRun } from "@/interfaces/TaskRun";
import humanizeDuration from "humanize-duration";
import { Temporal } from "@js-temporal/polyfill";
import VueFeather from "vue-feather";
import PrettifyJSON from "@/components/PrettifyJSON.vue";
import TasksConfigModal from "@/components/TasksConfigModal.vue";
import { socket } from "@/helpers/websocket";
import { PermissionsError, TimeoutError } from "@/errors/errors";

type SortKey = "RuleName" | "TaskName" | "Sensor" | "Outcome" | "StartedAt" | "EndedAt" | "Duration" | "StartTime";

type TaskRunFilters = {
  TaskName: string | null;
  RuleName: string | null;
  Outcome: string | null;
  State: string;
};

const N_LATEST = 5;

export default defineComponent({
  name: "TasksPage",
  components: {
    VueFeather,
    PrettifyJSON,
    TasksConfigModal,
  },
  setup() {
    const state = reactive({
      sensors: {} as Record<number, Sensor>,
      sensor_tag_options: {} as Record<string, string[]>, // list of '{"k": "v"}' JSON strings
      tasks: [] as Task[],
      task_rules: [] as TaskRule[],
      task_rules_loading: true as boolean,
      show_stats: true as boolean,
      selected_rule: null as TaskRule | null,
      selected_run: null as LocalTaskRun | null,
      task_runs: [] as LocalTaskRun[],
      task_runs_loading: true as boolean,
      // Live socket updates to task_runs and backlog counts are dropped while the rule-config
      // modal is open. On a busy tenant those fire many times per second, and every one of
      // them triggers a reactive recompute of the table's memoised aggregates — with the modal
      // rendered inside the same Vue app, that stalls the modal's inputs. onClearSelectedRule
      // refetches once to reconcile.
      taskRunUpdatesPaused: false as boolean,
      task_backlog_counts: {} as Record<number, number>,
      sort_key: "StartTime" as SortKey,
      reverse: false as boolean,
      filters: {} as TaskRunFilters,
      history_length: Temporal.Duration.from("PT6H") as Temporal.Duration,
      showRunLogsModal: false as boolean,
      logs_loading: false as boolean,
      logs_error: "" as string,
      errorMsg: "" as string,
    });

    const filteredTaskRuns = computed(() => {
      const filtered = state.task_runs
        .filter((run: TaskRun) => {
          if (state.filters.TaskName !== undefined && run.TaskName != state.filters.TaskName) {
            return false;
          }
          if (state.filters.RuleName !== undefined && run.RuleName != state.filters.RuleName) {
            return false;
          }
          if (state.filters.Outcome !== undefined && run.Outcome != state.filters.Outcome) {
            return false;
          }
          return true;
        })
        .sort((a, b) => {
          if (state.sort_key == "StartedAt") {
            if (!a.StartedAt) return 1;
            if (!b.StartedAt) return -1;
            return Temporal.Instant.compare(a.StartedAt, b.StartedAt) * (state.reverse ? -1 : 1);
          }
          if (state.sort_key == "Duration") {
            if (!a.Duration) return 1;
            if (!b.Duration) return -1;
            return Temporal.Duration.compare(a.Duration, b.Duration) * (state.reverse ? -1 : 1);
          }
          let [aSortValue, bSortValue] = [a[state.sort_key], b[state.sort_key]];
          if (aSortValue === null) {
            aSortValue = "";
          }
          if (bSortValue === null) {
            bSortValue = "";
          }
          return (aSortValue > bSortValue ? 1 : -1) * (state.reverse ? -1 : 1);
        });
      return filtered;
    });

    const getTaskRules = async () => {
      try {
        const data = await fetchTaskRules();
        state.task_rules = data;
      } catch (error) {
        if (error instanceof PermissionsError) {
          state.errorMsg = "You do not have permission to manage tasks";
        }
      }
      state.task_rules_loading = false;
    };

    const getTaskRuns = async () => {
      const params = new URLSearchParams();
      params.set("StartedSince", Temporal.Now.instant().subtract(state.history_length).epochSeconds.toString());
      // Sort by started_at so the LIMIT keeps the runs that started most recently, matching the StartedSince filter.
      params.set("OrderBy", "started_at");
      try {
        const data = await fetchTaskRuns(params);
        // Edit the actual run objects, rather than using display methods, so that we can easily sort/filter them
        state.task_runs = data.map((run) => formatTaskRun(run));
      } catch (error) {
        if (error instanceof PermissionsError) {
          state.errorMsg = "You do not have permission to manage tasks";
        }
      }
      state.task_runs_loading = false;
    };

    const getQueuedCount = async () => {
      try {
        const params = new URLSearchParams();
        params.set("Queued", "yes");
        const data = await fetchTaskRunCounts(params);
        state.task_backlog_counts = Object.fromEntries(
          state.task_rules
            .filter((rule): rule is TaskRule & { TaskRuleID: number } => rule.TaskRuleID != null)
            .map((rule) => [rule.TaskRuleID, (data as Record<string, number>)[String(rule.TaskRuleID)] ?? 0])
        );
      } catch (error) {
        if (error instanceof PermissionsError) {
          state.errorMsg = "You do not have permission to manage tasks";
        }
      }
    };

    const formatTaskRun = (run: TaskRun): LocalTaskRun => {
      const formattedTaskRun = run as LocalTaskRun;
      if (run.State === "running") {
        formattedTaskRun.Outcome = "running";
      }
      // Look up the sensor name and tags and turn them into a string on the run object
      formattedTaskRun.SensorName = run.SensorName;

      if (typeof run.SensorTags === "string") {
        formattedTaskRun.Sensor = JSON.parse(run.SensorTags);
      } else if (typeof run.SensorTags === "object" && run.SensorTags !== null) {
        formattedTaskRun.Sensor = run.SensorTags;
      } else {
        formattedTaskRun.Sensor = {};
      }

      if (run.StartedAt && run.EndedAt) {
        formattedTaskRun.Duration = Temporal.Instant.from(run.StartedAt).until(Temporal.Instant.from(run.EndedAt));
      } else {
        formattedTaskRun.Duration = null;
      }
      return formattedTaskRun;
    };

    const resolvedFailureIds = computed(() => {
      // Build map of (inputRow-taskRuleId) -> highest successful TaskRunID
      const successMap = new Map<string, number>();
      for (const run of state.task_runs) {
        if (run.Outcome === "success") {
          const key = `${run.InputRow}-${run.TaskRuleID}`;
          const existing = successMap.get(key) || 0;
          if (run.TaskRunID > existing) {
            successMap.set(key, run.TaskRunID);
          }
        }
      }
      // Find failed runs where a later success exists for the same data+rule
      const resolved = new Set<number>();
      for (const run of state.task_runs) {
        if (run.Outcome === "error") {
          const key = `${run.InputRow}-${run.TaskRuleID}`;
          const successId = successMap.get(key);
          if (successId !== undefined && successId > run.TaskRunID) {
            resolved.add(run.TaskRunID);
          }
        }
      }
      return resolved;
    });

    const isResolvedFailure = (run: LocalTaskRun): boolean => {
      return resolvedFailureIds.value.has(run.TaskRunID);
    };

    type RuleStats = {
      running: number;
      success: number;
      error: number;
      resolvedFailures: number;
      averageDuration: Temporal.Duration | null;
      longestDuration: Temporal.Duration | null;
      latestOutcomes: string[];
    };

    const EMPTY_STATS: RuleStats = {
      running: 0,
      success: 0,
      error: 0,
      resolvedFailures: 0,
      averageDuration: null,
      longestDuration: null,
      latestOutcomes: [],
    };

    // Aggregate every per-rule stat the table needs in a SINGLE pass over task_runs, memoised as a
    // computed. Each stats cell used to call its own filter/sort helper, so the table cost O(rules × runs)
    // and re-ran all of it on every render — including every websocket task-run update. Now it's one
    // O(runs) pass that only recomputes when task_runs (or the resolved-failure set) actually changes.
    const ruleStats = computed(() => {
      const resolved = resolvedFailureIds.value;

      type Accumulator = {
        running: number;
        success: number;
        error: number;
        resolvedFailures: number;
        durationSumMs: number;
        durationCount: number;
        longestMs: number;
        longest: Temporal.Duration | null;
        outcomes: { startedAt: string | null; outcome: string }[];
      };
      const newAccumulator = (): Accumulator => ({
        running: 0,
        success: 0,
        error: 0,
        resolvedFailures: 0,
        durationSumMs: 0,
        durationCount: 0,
        longestMs: -1,
        longest: null,
        outcomes: [],
      });

      const byRuleAcc = new Map<number, Accumulator>();
      for (const run of state.task_runs) {
        let acc = byRuleAcc.get(run.TaskRuleID);
        if (!acc) {
          acc = newAccumulator();
          byRuleAcc.set(run.TaskRuleID, acc);
        }
        if (run.Outcome == "running") {
          acc.running++;
        } else if (run.Outcome == "success") {
          acc.success++;
        } else if (run.Outcome == "error") {
          acc.error++;
          if (resolved.has(run.TaskRunID)) {
            acc.resolvedFailures++;
          }
        }
        if (run.Duration !== null) {
          const ms = run.Duration.total("millisecond");
          acc.durationSumMs += ms;
          acc.durationCount++;
          if (ms > acc.longestMs) {
            acc.longestMs = ms;
            acc.longest = run.Duration;
          }
        }
        if (run.Outcome != "running") {
          acc.outcomes.push({ startedAt: run.StartedAt, outcome: run.Outcome });
        }
      }

      const finalize = (acc: Accumulator): RuleStats => ({
        running: acc.running,
        success: acc.success,
        error: acc.error,
        resolvedFailures: acc.resolvedFailures,
        averageDuration:
          acc.durationCount == 0
            ? null
            : Temporal.Duration.from({ milliseconds: Math.round(acc.durationSumMs / acc.durationCount) }),
        longestDuration: acc.longest,
        latestOutcomes: acc.outcomes
          .sort((a, b) => {
            if (!a.startedAt) return 1;
            if (!b.startedAt) return -1;
            return Temporal.Instant.compare(a.startedAt, b.startedAt);
          })
          .slice(-N_LATEST)
          .map((o) => o.outcome),
      });

      // Totals ("All rules") fold the per-rule accumulators — O(rules), not another pass over runs.
      // Latest-outcomes is left empty for totals since that column is blank on the totals row.
      const totals = newAccumulator();
      const byRule = new Map<number, RuleStats>();
      for (const [ruleID, acc] of byRuleAcc) {
        byRule.set(ruleID, finalize(acc));
        totals.running += acc.running;
        totals.success += acc.success;
        totals.error += acc.error;
        totals.resolvedFailures += acc.resolvedFailures;
        totals.durationSumMs += acc.durationSumMs;
        totals.durationCount += acc.durationCount;
        if (acc.longestMs > totals.longestMs) {
          totals.longestMs = acc.longestMs;
          totals.longest = acc.longest;
        }
      }

      return { byRule, totals: finalize(totals) };
    });

    // Pair each rule with its precomputed stats so the template reads O(1) per row instead of calling
    // the old per-cell helpers.
    const rulesWithStats = computed(() =>
      state.task_rules.map((rule) => ({
        rule,
        stats: (rule.TaskRuleID != null ? ruleStats.value.byRule.get(rule.TaskRuleID) : undefined) ?? EMPTY_STATS,
      }))
    );

    const sortBy = (key: SortKey) => {
      state.reverse = state.sort_key == key ? !state.reverse : false;
      state.sort_key = key;
    };

    const showConfigureRule = (rule: TaskRule) => {
      state.taskRunUpdatesPaused = true;
      state.selected_rule = rule;
    };

    const showNewRule = () => {
      state.taskRunUpdatesPaused = true;
      state.selected_rule = {
        Enabled: true,
        InputTable: null,
        Name: "New rule",
        Parameters: {},
        SensorIDs: null,
        SensorTags: {},
        TaskAvailable: null,
        TaskID: null,
        TaskName: null,
        TaskRuleID: null,
        StartTime: "",
        DataTaskRuleIDs: [null],
        MultipleTruePerBatch: true,
      };
    };

    const showRunLogs = async (run: LocalTaskRun) => {
      state.selected_run = run;
      state.showRunLogsModal = true;
      // Reset the error before deciding whether to fetch, so a cached run never opens under a stale
      // failure banner left by a previous run.
      state.logs_error = "";
      // Logs aren't in the list payload; fetch them on demand. Skip if we already loaded them for this run.
      if (run.Logs != null) {
        // Clear any loading flag left behind by a fetch whose modal was closed before it settled.
        state.logs_loading = false;
        return;
      }
      state.logs_loading = true;
      try {
        const detail = await fetchTaskRun(run.TaskRunID);
        run.Logs = detail.Logs;
      } catch (error) {
        // A newer run may have been opened while this request was in flight; only surface the error if
        // this run is still the selected one, so an overlapping fetch can't clobber the current view.
        if (state.selected_run === run) {
          if (error instanceof PermissionsError) {
            state.logs_error = "You do not have permission to view these logs";
          } else if (error instanceof TimeoutError) {
            state.logs_error = "Timed out loading logs — the server may be busy. Try again.";
          } else {
            state.logs_error = "Failed to load logs";
          }
        }
      } finally {
        if (state.selected_run === run) {
          state.logs_loading = false;
        }
      }
    };

    const rerun = (run: LocalTaskRun) => {
      postRerunTask(run.TaskRunID);
    };

    const onClearSelectedRule = () => {
      state.selected_rule = null;
      state.taskRunUpdatesPaused = false;
      // Reconcile the table with any updates the socket dropped while the modal was open.
      // Fire-and-forget — the loading spinner isn't shown here since the table is already
      // populated (just stale); replacing it in place is smoother than flashing empty.
      getTaskRuns()
        .then(getQueuedCount)
        .catch(() => {
          /* transient failure is fine; the next socket update or manual refresh recovers */
        });
    };

    const format_duration = (duration: Temporal.Duration): string => {
      if (duration === null || duration === undefined || duration.blank) {
        return "-";
      }
      if (duration.total("second") > 60) {
        duration = duration.round("second");
      } else {
        duration = duration.round({
          roundingIncrement: 100,
          smallestUnit: "millisecond",
        });
      }
      return humanizeDuration(duration.total("millisecond"), { largest: 2 });
    };

    const incrementBacklogCount = (taskRuleID: number) => {
      if (state.task_backlog_counts[taskRuleID] !== undefined) {
        state.task_backlog_counts[taskRuleID] += 1;
      } else if (state.task_backlog_counts[taskRuleID] === undefined) {
        state.task_backlog_counts[taskRuleID] = 1;
      }
    };

    const decrementBacklogCount = (taskRuleID: number) => {
      if (state.task_backlog_counts[taskRuleID] > 0) {
        // > handles undefined correctly here
        state.task_backlog_counts[taskRuleID] -= 1;
      }
    };

    const handleNewTaskRun = (taskRun: TaskRun) => {
      if (state.taskRunUpdatesPaused) return;
      if (taskRun.StartedAt) {
        state.task_runs.unshift(formatTaskRun(taskRun));
      } else {
        incrementBacklogCount(taskRun.TaskRuleID);
      }
    };

    const handleUpdateTaskRun = (updatedTaskRun: TaskRun) => {
      if (state.taskRunUpdatesPaused) return;
      const formattedTaskRun = formatTaskRun(updatedTaskRun);
      const index = state.task_runs.findIndex((taskRun) => taskRun.TaskRunID === formattedTaskRun.TaskRunID);

      if (index < 0) {
        // Not in our task_runs list for some reason
        if (updatedTaskRun.StartedAt) {
          // If the task has started we need to first add it, which increments backlog, and the decrement to reflect the
          // fact it's actually running and not in the queue
          handleNewTaskRun(updatedTaskRun);
          decrementBacklogCount(updatedTaskRun.TaskRuleID);
        }
        return;
      }

      const wasStarted = !!state.task_runs[index].StartedAt;

      if (updatedTaskRun.StartedAt) {
        if (!wasStarted) {
          // genuinely transitioning from backlog → started
          decrementBacklogCount(updatedTaskRun.TaskRuleID);
        }
        state.task_runs[index] = formattedTaskRun;
      } else {
        if (wasStarted) {
          console.log("Removing task run that has been un-run");
          state.task_runs.splice(index, 1);
          incrementBacklogCount(updatedTaskRun.TaskRuleID);
        }
      }
    };

    socket.bind("new_task_run", handleNewTaskRun);
    socket.bind("updated_task_run", handleUpdateTaskRun);

    getTaskRuns().then(async () => {
      await getTaskRules();
      // Wait for task rules response before we get queued count so we know all the TaskRunIDs we need to set to zero
      // if no count is returned.
      await getQueuedCount();
    });

    onMounted(async () => {
      // Warm the tasks cache in the background so the rule-config modal opens with the list already
      // loaded instead of firing (and waiting on) a request each time it's opened.
      fetchTasks().catch(() => {
        /* modal's own fetch will surface any error when it's opened */
      });

      try {
        const data = await fetchSensorTagOptions();
        state.sensor_tag_options = data;
      } catch (error) {
        if (error instanceof PermissionsError) {
          state.errorMsg = "You do not have permission to manage tasks";
        }
      }
    });

    return {
      ...toRefs(state),
      getTaskRules,
      sortBy,
      showConfigureRule,
      showNewRule,
      showRunLogs,
      isResolvedFailure,
      ruleStats,
      rulesWithStats,
      rerun,
      onClearSelectedRule,
      formatTime,
      format_duration,
      filteredTaskRuns,
    };
  },
});
